import React, { useMemo, useState } from 'react';
import {
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';
import { formatDateIE, formatEuroCurrency } from '../../../utility/formatters';
import SubTenantsList from './SubTenantsList';
import EvictionRoadmapModal from './EvictionRoadmapModal';

export interface TenantCardTenant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  moveInDate: string;
  leaseEndDate?: string;
  rentAmount?: number;
  depositAmount?: number;
  status: 'active' | 'inactive';
  rtbRegisteredOn?: { _seconds: number; _nanoseconds: number };
  paymentStatus?: 'current' | 'late' | 'upcoming' | 'paid';
  subTenants?: Array<{
    id?: string;
    name: string;
    email: string;
    phone?: string;
  }>;
}

interface TenantCardProps {
  tenant: TenantCardTenant;
  propertyId: number | string;
  onEditTenancy: (tenant: TenantCardTenant) => void;
  onEditSubTenant: (tenant: TenantCardTenant) => void;
  onRemoveTenant: (tenant: TenantCardTenant) => void;
}

const TenantCard: React.FC<TenantCardProps> = ({
  tenant,
  propertyId,
  onEditTenancy,
  onEditSubTenant,
  onRemoveTenant,
}) => {
  const [isEvictionOpen, setIsEvictionOpen] = useState(false);
  const formatTimestamp = (dateVal?: { _seconds: number; _nanoseconds: number }) => {
    if (!dateVal) return '';
    return formatDateIE(new Date(dateVal._seconds * 1000).toISOString());
  };
  const tenancyStartDate = useMemo(() => new Date(tenant.moveInDate), [tenant.moveInDate]);
  const tenancyMonths = useMemo(() => {
    const now = new Date();
    const years = now.getFullYear() - tenancyStartDate.getFullYear();
    const months = now.getMonth() - tenancyStartDate.getMonth();
    const total = years * 12 + months + (now.getDate() >= tenancyStartDate.getDate() ? 0 : -1);
    return Math.max(total, 0);
  }, [tenancyStartDate]);
  const getPaymentStatusBadge = (status?: string) => {
    switch (status) {
      case 'current':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
            <CheckCircleIcon className="h-4 w-4" />
            Current
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
            <CheckCircleIcon className="h-4 w-4" />
            Paid
          </span>
        );
      case 'late':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
            <ExclamationTriangleIcon className="h-4 w-4" />
            Late
          </span>
        );
      case 'upcoming':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            <CalendarIcon className="h-4 w-4" />
            Due Soon
          </span>
        );
      default:
        return null;
    }
  };

  const isActive = tenant.status === 'active';

  return (
    <>
      <div
        className={`flex flex-col gap-4 rounded-2xl border p-6 shadow-sm transition hover:shadow-lg md:flex-row md:items-center ${
          isActive ? 'border-gray-100' : 'border-gray-200 bg-gray-50/50 opacity-75'
        }`}
      >
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h4 className={`text-lg font-semibold ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
              {tenant.name}
            </h4>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
            {isActive && getPaymentStatusBadge(tenant.paymentStatus)}
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            <div
              className={`flex items-center gap-2 text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
            >
              <EnvelopeIcon className="h-4 w-4 text-gray-400" />
              {tenant.email ? (
                <a href={`mailto:${tenant.email}`} className="hover:text-blue-600">
                  {tenant.email}
                </a>
              ) : (
                <span>-</span>
              )}
            </div>

            {tenant.phone && (
              <div
                className={`flex items-center gap-2 text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
              >
                <PhoneIcon className="h-4 w-4 text-gray-400" />
                <a href={`tel:${tenant.phone}`} className="hover:text-blue-600">
                  {tenant.phone}
                </a>
              </div>
            )}

            <div
              className={`flex items-center gap-2 text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
            >
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              Moved in: {formatDateIE(tenant.moveInDate)}
            </div>

            {tenant.leaseEndDate && (
              <div
                className={`flex items-center gap-2 text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
              >
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                Lease ends: {tenant.leaseEndDate ? formatDateIE(tenant.leaseEndDate) : ''}
              </div>
            )}

            {tenant.rentAmount && (
              <div
                className={`flex items-center gap-2 text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
              >
                <BanknotesIcon className="h-4 w-4 text-gray-400" />
                Rent: {formatEuroCurrency(tenant.rentAmount)}/month
              </div>
            )}

            {tenant.depositAmount && (
              <div
                className={`flex items-center gap-2 text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'}`}
              >
                <BanknotesIcon className="h-4 w-4 text-gray-400" />
                Deposit: {formatEuroCurrency(tenant.depositAmount)}
              </div>
            )}

            <div
              className={`flex items-center gap-2 text-sm ${
                isActive ? 'text-gray-600' : 'text-gray-500'
              }`}
            >
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              {tenant.rtbRegisteredOn ? (
                <span>RTB Registered: {formatTimestamp(tenant.rtbRegisteredOn)}</span>
              ) : (
                <span className="text-amber-600 font-medium">
                  RTB Registered: Not provided (Required for compliance reminders)
                </span>
              )}
            </div>
          </div>

          {tenant.subTenants && tenant.subTenants.length > 0 && (
            <SubTenantsList
              subTenants={tenant.subTenants}
              isActive={isActive}
              tenancyId={tenant.id}
              propertyId={propertyId}
            />
          )}
        </div>

        <div className="flex flex-wrap gap-2 md:flex-col">
          <button
            onClick={() => onEditTenancy(tenant)}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-50"
          >
            <CalendarIcon className="h-4 w-4" />
            Edit Tenancy
          </button>
          <button
            onClick={() => onEditSubTenant(tenant)}
            className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Sub-tenant
          </button>
          <button
            onClick={() => onRemoveTenant(tenant)}
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
          >
            <UserMinusIcon className="h-4 w-4" />
            Remove
          </button>
          <button
            onClick={() => setIsEvictionOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
          >
            <ExclamationTriangleIcon className="h-4 w-4" />
            Eviction Roadmap
          </button>
        </div>
      </div>
      <EvictionRoadmapModal
        isOpen={isEvictionOpen}
        onClose={() => setIsEvictionOpen(false)}
        tenant={tenant}
        propertyId={propertyId}
        tenancyMonths={tenancyMonths}
      />
    </>
  );
};

export default TenantCard;
